// Authoring shortcuts for a paged HTTP service. A preset writes into the two expression fields and is
// then forgotten: nothing records which one an author started from, and both fields stay editable
// underneath. That is the whole difference between this and a named-mode model — adding a preset is a
// line in this file, and changing one breaks nothing already saved.
import type { HttpServiceModelPaging, NextPageRequest } from '@/types'

export interface PagingPreset {
  id: string
  label: string
  /** What this vendor shape looks like, so an author can recognise theirs without reading the expressions. */
  hint: string
  /** The predicate that says another page follows. Empty for the custom preset, which writes nothing. */
  hasNextPageExpression: string
  nextPage: NextPageRequest
}

export const pagingPresets: PagingPreset[] = [
  {
    id: 'nextUrl',
    label: 'Next URL from the response',
    hint: 'The vendor hands back the whole address of the next page (Microsoft Graph, HubSpot, SharePoint).',
    hasNextPageExpression: "response.body['@odata.nextLink']",
    nextPage: { url: "response.body['@odata.nextLink']", query: {} },
  },
  {
    id: 'cursor',
    label: 'Cursor into a query parameter',
    hint: 'The vendor hands back a token the next request carries, and stops when a page comes back empty (Concur).',
    hasNextPageExpression: 'response.body.Resources.length > 0',
    nextPage: { query: { cursor: 'response.body.nextCursor' } },
  },
  {
    id: 'pageNumber',
    label: 'Page number',
    hint: 'The request carries a page number counting from one, and a short page is the last (Paylocity).',
    hasNextPageExpression: 'response.body.items.length == 100',
    nextPage: { query: { page: 'paging.page + 1' } },
  },
  {
    id: 'rowOffset',
    label: 'Row offset',
    hint: 'The request carries how many rows to skip, and a short page is the last (ADP).',
    hasNextPageExpression: 'response.body.items.length == 100',
    nextPage: { query: { $skip: 'paging.page * 100' } },
  },
  {
    id: 'declaredTotal',
    label: 'Offset against a declared total',
    hint: 'The vendor names how many rows there are, and the offset counts up to it (Mailchimp).',
    hasNextPageExpression: 'response.body.total_items > paging.page * 100',
    nextPage: { query: { offset: 'paging.page * 100' } },
  },
  {
    id: 'bodyOffset',
    label: 'Offset inside the request body',
    hint: 'The vendor pages inside a JSON body, so the whole body is rebuilt each page (HubSpot Lists).',
    hasNextPageExpression: 'response.body.hasMore',
    nextPage: { query: {}, body: 'jsonEncode({ offset: response.body.offset, count: 500 })' },
  },
  {
    id: 'custom',
    label: 'Custom',
    hint: 'Write both fields directly, for a vendor no preset fits.',
    hasNextPageExpression: '',
    nextPage: { query: {} },
  },
]

/** The scope a paging expression reads, offered as completions on both fields. Authors may type a
 *  deeper path (`response.body.d.__next`); the fields are free strings. */
export const pagingRoots: { path: string; hint: string }[] = [
  { path: 'response.body', hint: 'parsed body of the page just fetched' },
  { path: 'response.headers', hint: 'response headers of the page just fetched' },
  { path: 'response.statusCode', hint: 'HTTP status code of the page just fetched' },
  { path: 'paging.page', hint: 'pages already fetched — 0 while the first request is built' },
]

/**
 * Writes a preset into the model. The custom preset writes nothing, so an author who reaches it after
 * starting from another keeps what they have. Every other preset replaces both fields outright: a
 * half-applied preset is a shape no vendor pages by.
 *
 * The query map is copied rather than shared, so editing one service's parameters cannot reach into the
 * preset every other service is about to read.
 */
export function applyPreset(
  model: HttpServiceModelPaging,
  preset: PagingPreset,
): HttpServiceModelPaging {
  if (preset.id === 'custom') return model

  return {
    ...model,
    hasNextPageExpression: preset.hasNextPageExpression,
    nextPage: {
      url: preset.nextPage.url,
      body: preset.nextPage.body,
      query: { ...preset.nextPage.query },
    },
  }
}
